// SPDX-License-Identifier: AGPL-3.0-or-later

import {LimitResolver} from '@app/features/app/utils/LimitResolverAdapter';
import {readBlobAsBase64NoPrefix} from '@app/features/expressions/utils/ImageUploadFileUtils';
import styles from '@app/features/guild/components/GuildNotificationSoundControl.module.css';
import {TRY_AGAIN_IN_A_MOMENT_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {openFilePicker} from '@app/features/messaging/utils/FilePickerUtils';
import {
	ENTRANCE_SOUND_FILE_PICKER_ACCEPT,
	isValidEntranceSoundFile,
} from '@app/features/notification/utils/EntranceSoundClientValidators';
import {loadGuildNotificationSound} from '@app/features/notification/utils/GuildNotificationSound';
import {SoundType} from '@app/features/notification/utils/SoundUtils';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {Button} from '@app/features/ui/button/Button';
import * as SoundCommands from '@app/features/ui/commands/SoundCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {handleAccountLimitedError} from '@app/features/user/utils/AccountLimitUtils';
import {showUserErrorModal} from '@app/features/user/utils/UserErrorModalUtils';
import {openEntranceSoundTrimmerModal} from '@app/features/voice/components/EntranceSoundTrimmerModal';
import {
	GUILD_NOTIFICATION_SOUND_MAX_DURATION_MS,
	SOUNDBOARD_MAX_SIZE_BYTES,
} from '@fluxer/constants/src/SoundboardConstants';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useState} from 'react';

const UPLOAD_DESCRIPTOR = msg({
	message: 'Upload sound',
	comment: 'Button that opens the file picker for a community or channel notification sound.',
});
const REPLACE_DESCRIPTOR = msg({
	message: 'Replace',
	comment: 'Button that replaces the current community or channel notification sound.',
});
const LISTEN_DESCRIPTOR = msg({
	message: 'Listen',
	comment: 'Button that plays the current community or channel notification sound.',
});
const REMOVE_DESCRIPTOR = msg({
	message: 'Remove',
	comment: 'Button that removes the current community or channel notification sound.',
});
const NEEDS_BOOST_DESCRIPTOR = msg({
	message: 'Requires boost level 3.',
	comment: 'Hint under a community or channel notification sound control when the community lacks the boost level.',
});
const SAVED_DESCRIPTOR = msg({
	message: 'Notification sound saved',
	comment: 'Toast after a community or channel notification sound is saved.',
});
const REMOVED_DESCRIPTOR = msg({
	message: 'Notification sound removed',
	comment: 'Toast after a community or channel notification sound is removed.',
});
const SAVE_FAILED_DESCRIPTOR = msg({
	message: "Couldn't change the notification sound",
	comment: 'Error title when a community or channel notification sound could not be saved or removed.',
});
const LOAD_FAILED_DESCRIPTOR = msg({
	message: "Couldn't play the notification sound",
	comment: 'Error title when the current community or channel notification sound could not be downloaded.',
});
const INVALID_FILE_DESCRIPTOR = msg({
	message: 'Unsupported audio file.',
	comment: 'Error body when the file picked for a community or channel notification sound cannot be used.',
});
const TRIM_TITLE_DESCRIPTOR = msg({
	message: 'Trim notification sound',
	comment: 'Title of the modal that crops an audio file down to the notification sound length cap.',
});
const TRIM_HELP_DESCRIPTOR = msg({
	message: 'Drag the handles to choose up to 2 seconds. Volume is evened out when you save.',
	comment: 'Help text in the notification sound trimmer. The server limits the loudness of the saved sound.',
});

const logger = new Logger('GuildNotificationSoundControl');

interface GuildNotificationSoundControlProps {
	guildId: string;
	hash: string | null;
	onSave: (audio: string) => Promise<void>;
	onRemove: () => Promise<void>;
	dataFlx: string;
}

export const GuildNotificationSoundControl: React.FC<GuildNotificationSoundControlProps> = observer(
	({guildId, hash, onSave, onRemove, dataFlx}) => {
		const {i18n} = useLingui();
		const [busy, setBusy] = useState(false);
		const boosted =
			LimitResolver.resolve({
				key: 'feature_guild_notification_sound',
				fallback: 0,
				context: {guildId},
				evaluationContext: 'guild',
			}) > 0;

		const change = async (action: () => Promise<void>, doneMessage: string): Promise<void> => {
			setBusy(true);
			try {
				await action();
				ToastCommands.createToast({type: 'success', children: doneMessage});
			} catch (error) {
				logger.error('Failed to change notification sound', error);
				if (!handleAccountLimitedError(error)) {
					showUserErrorModal(i18n._(SAVE_FAILED_DESCRIPTOR), i18n._(TRY_AGAIN_IN_A_MOMENT_DESCRIPTOR));
				}
			} finally {
				setBusy(false);
			}
		};

		const pick = async (): Promise<void> => {
			const [file] = await openFilePicker({accept: ENTRANCE_SOUND_FILE_PICKER_ACCEPT});
			if (!file) return;
			if (!isValidEntranceSoundFile(file).valid) {
				showUserErrorModal(i18n._(SAVE_FAILED_DESCRIPTOR), i18n._(INVALID_FILE_DESCRIPTOR));
				return;
			}
			openEntranceSoundTrimmerModal({
				sourceFile: file,
				title: i18n._(TRIM_TITLE_DESCRIPTOR),
				help: i18n._(TRIM_HELP_DESCRIPTOR),
				maxDurationMs: GUILD_NOTIFICATION_SOUND_MAX_DURATION_MS,
				maxBytes: SOUNDBOARD_MAX_SIZE_BYTES,
				onConfirm: async ({blob}) => {
					const audio = await readBlobAsBase64NoPrefix(blob);
					await change(() => onSave(audio), i18n._(SAVED_DESCRIPTOR));
				},
			});
		};

		const listen = async (): Promise<void> => {
			if (!hash) return;
			setBusy(true);
			const url = await loadGuildNotificationSound(guildId, hash);
			setBusy(false);
			if (!url) {
				showUserErrorModal(i18n._(LOAD_FAILED_DESCRIPTOR), i18n._(TRY_AGAIN_IN_A_MOMENT_DESCRIPTOR));
				return;
			}
			SoundCommands.stopAllSounds();
			SoundCommands.previewSound(SoundType.GuildMessage, url);
		};

		return (
			<div className={styles.control} data-flx={`${dataFlx}.control`}>
				<div className={styles.actions} data-flx={`${dataFlx}.actions`}>
					<Button
						variant="secondary"
						small
						onClick={pick}
						disabled={busy || !boosted}
						data-flx={`${dataFlx}.upload-button`}
					>
						{i18n._(hash ? REPLACE_DESCRIPTOR : UPLOAD_DESCRIPTOR)}
					</Button>
					{hash && (
						<>
							<Button variant="secondary" small onClick={listen} disabled={busy} data-flx={`${dataFlx}.listen-button`}>
								{i18n._(LISTEN_DESCRIPTOR)}
							</Button>
							<Button
								variant="danger"
								small
								onClick={() => change(onRemove, i18n._(REMOVED_DESCRIPTOR))}
								disabled={busy}
								data-flx={`${dataFlx}.remove-button`}
							>
								{i18n._(REMOVE_DESCRIPTOR)}
							</Button>
						</>
					)}
				</div>
				{!boosted && (
					<p className={styles.hint} data-flx={`${dataFlx}.boost-hint`}>
						{i18n._(NEEDS_BOOST_DESCRIPTOR)}
					</p>
				)}
			</div>
		);
	},
);

GuildNotificationSoundControl.displayName = 'GuildNotificationSoundControl';
